
export type AbilityScoreType = "STR" | "DEX" | "CON" | "INT" | "WIS" | "CHA";
export type RollType = "Attack" | "Saving Throw" | "Ability Check" | "Skill Check";
export type AbilityScoreExpandedType = AbilityScoreType | "Spellcasting" | "Same as Feat" | "Selectable" | "STR or DEX" | "INT, WIS, or CHA" | "Your Choice" | "None" | "Attack";
export type DefenseType = AbilityScoreType | "Physical" | "Mental" | "Spiritual";
export type ActionType = "Attack" | "Ability" | "Magic" | "Movement" | "Item Action" | "None" | "Inherit";
export type AttackType = "Melee" | "Ranged" | "Melee Spell" | "Ranged Spell" | "Bonus" | "Save" | "Thrown" | "Light" | "Versatile" | "None";
export type RangeType = "Touch" | "Self" | "Unlimited" | "Spaces";
export type SizeType = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8; // 0 = Not Set, 1 = Tiny, 2 = Small, 3 = Medium, 4 = Large, 5 = Huge, 6 = Gargantuan, 7 || 8 = Size set in other location
export const isSizeType = (num: number): num is SizeType => {
  return num === 0 ||
    num === 1 ||
    num === 2 ||
    num === 3 ||
    num === 4 ||
    num === 5 ||
    num === 6 ||
    num === 7 ||
    num === 8;
}
export type SpecialCastingTime = "Reaction" | "Mini Rest" | "Short Rest" | "Long Rest" | "Down Time" | "Initiative" | "Round" | "None";
export type RefreshRuleType = "Short Rest" | "Long Rest" | "Dawn" | "1 Hour" | "2 Hours" | "8 Hours" | "24 Hours" |  "Special" | "None";
export type KnowledgeType = "Adaptation" | "Transform" | "Skill" | "Tool" | "Language" | "Spell" | "Creature" | "Crafting Recipe" | "None";
export type CreatureType = "Humanoid" | "Beast" | "Construct" | "Aberration" | "Undead" | "Dragon" | "Monstrosity" | "Elemental" | 
  "Fey" | "Giant" | "Ooze" | "Plant" | "Shapechanger" | "Celestial" | "Fiend" | "None";

export const isCreatureType = (str: string): str is CreatureType => {
  return str === "Humanoid" ||
    str === "Beast" ||
    str === "Construct" ||
    str === "Aberration" ||
    str === "Undead" ||
    str === "Dragon" ||
    str === "Monstrosity" ||
    str === "Elemental" ||
    str === "Fey" ||
    str === "Giant" ||
    str === "Ooze" ||
    str === "Plant" ||
    str === "Shapechanger" ||
    str === "Celestial" ||
    str === "Fiend" ||
    str === "None";
}

export type ItemType = "Armor" | "Weapon" | "Ammunition" | "Consumable" | 
  "Ring" | "Rod" | "Potion" | "Poison" | "Scroll" | "Dust" | "Staff" | 
  "Tool" | "Artisan's Tools" | "Gaming Set" | "Musical Instrument" | 
  "Vehicle" | "Wand" | "Wondrous" | "Shoe" | "Cape/Cloak" | "Gloves" | 
  "Headgear" | "Necklace" | "Belt" | "Manual" | "Container" | 
  "Bracers" | "Gem" | "Holy Symbol" | "Arcane Focus" | "Druidic Focus" | 
  "Other" | "Custom" | "Deck" | "Card" | "Book"; // | "Prosthesis";

export const isItemType = (str: string): str is ItemType => {
  return str === "Armor" ||
    str === "Weapon" ||
    str === "Ammunition" ||
    str === "Consumable" ||
    str === "Ring" ||
    str === "Rod" ||
    str === "Potion" ||
    str === "Poison" ||
    str === "Scroll" ||
    str === "Dust" ||
    str === "Staff" ||
    str === "Tool" ||
    str === "Artisan's Tools" ||
    str === "Gaming Set" ||
    str === "Musical Instrument" ||
    str === "Vehicle" ||
    str === "Wand" ||
    str === "Wondrous" ||
    str === "Shoe" ||
    str === "Cape/Cloak" ||
    str === "Gloves" ||
    str === "Headgear" ||
    str === "Necklace" ||
    str === "Belt" ||
    str === "Manual" ||
    str === "Container" ||
    str === "Bracers" ||
    str === "Gem" ||
    str === "Holy Symbol" ||
    str === "Arcane Focus" ||
    str === "Druidic Focus" ||
    str === "Other" ||
    str === "Custom" ||
    str === "Deck" ||
    str === "Card" ||
    str === "Book";
}

export type WeaponType = "Battleaxe" | "Blowgun" | "Club" | "Crossbow, Hand" | "Crossbow, Heavy" | 
  "Crossbow, Light" | "Dagger" | "Dart" | "Flail" | "Glaive" | "Greataxe" | "Greatclub" | "Greatsword" | 
  "Halberd" | "Handaxe" | "Javelin" | "Lance" | "Light Hammer" | "Longbow" | "Longsword" | "Mace" | "Maul" | 
  "Morningstar" | "Pike" | "Quarterstaff" | "Rapier" | "Scimitar" | "Shortbow" | "Shortsword" | "Sickle" | 
  "Sling" | "Spear" | "Trident" | "War Pick" | "Warhammer" | "Whip" | "None";
export type AmmunitionType = "Arrow" | "Crossbow Bolt" | "Sling Bullet" | "Bullet" | "Stone" | "Needle" | "Special" | "Ōdzutsu Shot" | "None";
export type BookType = "Spell" | "Creature" | "Item" | "Other";
export type CastingTime = 'A' | 'BA' | 'RA' | 'Special' | 'Attack' | 
  'Short Rest' | 'Long Rest' | 'Initiative' | "Start of Turn" | "End of Turn" | 
  "Passive" | "On Struck" | "1 Minute" | "10 Minutes" | "1 Hour";
export type CastingTimeWithStandardSpecials = CastingTime | "1 Minute" | "10 Minutes" | "1 Hour";
export type TargetType = "X Targets" | "Area" | "Self" | "None";
export type AreaType = "Cone" | "Line" | "Cube" | "Square" | "Sphere" | "Circle" | "Cylinder" | "Wall" | "None";

export type SpellLevel = - 1 | 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
export type SpellLevelString = "Cantrip" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9";
export type SchoolOfMagic = "Conjuration" | "Necromancy" | "Evocation" | "Abjuration" | "Transmutation" | "Divination" | "Enchantment" | "Illusion";

export type SelectableType = "Ability Score" | "Damage Type" | "School of Magic" | "Dragon Type" | "Dragon Breed" | "Strings" | "Spell List" | "Race" | "Item" | "Weapon Type" | "Spell" | "Character Spell" | "Number" | "Dice Size";
export type DamageType = "Bludgeoning" | "Piercing" | "Slashing" | "Acid" | "Cold" | "Fire" | "Force" | "Lightning" | "Necrotic" | "Poison" | "Psychic" | "Radiant" | "Thunder" | "Typeless" | "Same as Main";
export type EffectType = "None" | "Control" | "Summon" | "Teleport" | "Portal" | "Transform" | 
  "Create Resource" | "Utility" | "Create Item" | "Enhance Item" | "Enhance Creature" | "Healing" | "Max HP" | "Temp HP" | 
  "Bonus Roll" | "Self Condition" | "Self Gift" | "Special" | "Selectable Key" | "Rollable Table" | "Use Extra" | 
  "Draw Card" | "Damage"; // DamageType;
export type DurationType = "Instantaneous" | "1 Round" | "2 Rounds" | "1 Minute" | "10 Minutes" | "1 Hour" | "8 Hours" | "24 Hours" | "Until Dispelled" | "Special";

export type PotenceType = "Slot" | "Character" | "Source" | "Class" | "None";

export const isPotenceType = (str: string): str is PotenceType => {
  return str === "Slot" ||
    str === "Character" ||
    str === "Source" ||
    str === "Class" ||
    str === "None";
}

export type ModifierTarget = "AC" | "Med Armor Max AC from DEX" | "Max HP" | "Speed" | 
  "Ability Check" | "Skill Check" | "Saving Throw" | "Ability Score" | "Ability Score Max" | 
  "Spell DC" | "Attack" | "Damage" | "Range" | "Initiative" | "Attunement Slots" | "Table Value" | 
  "Formula Value" | "Passive Perception" | "Passive Insight" | "Passive Investigation" | 
  "Size" | "Size Bonus For Carrying" | "Carrying Capacity" | "Max Prepared Spells";

export type SpeedType = "walk" | "fly" | "swim" | "climb" | "burrow";

export const isSpeedType = (str: string): str is SpeedType => {
  return str === "walk" ||
    str === "fly" ||
    str === "swim" ||
    str === "climb" ||
    str === "burrow";
}

export type CreatureFBType = "Traits" | "Actions" | "Legendary Actions" | "Lair Actions" | "Bonus Actions" | "Reactions";

export const isCreatureFBType = (str: string): str is CreatureFBType => {
  return str === "Traits" ||
    str === "Actions" ||
    str === "Legendary Actions" ||
    str === "Lair Actions" ||
    str === "Bonus Actions" ||
    str === "Reactions";
}

export type RecipeComponentType = "Any" | "Animus" | "Ash" | "Fluid" | "Organ" | "Hair" | "Bone" | "Hide" | "Natural Weapon" | "Trophy" | "Other" | "None";

export type CharacterLimitationType = "game_class" | "subclass" | "class_group" | "class_or_class_group" | 
  "race" | "subrace" | "gift" | "feat" | "alignment" | "min_ability_score" | "spellcaster" | "spells_of_schools" | 
  "spell" | "proficiency" | "at_least_1_weapon" | "at_least_1_language" | "level" | "pact_boon" | 
  "eldritch_invocation" | "tags" | "dragonmark" | "wild_talent" | "feature_of_type" | "";

export const isCharacterLimitationType = (str: string): str is CharacterLimitationType => {
  return str === "game_class" ||
    str === "subclass" ||
    str === "class_group" ||
    str === "class_or_class_group" ||
    str === "race" ||
    str === "subrace" ||
    str === "gift" ||
    str === "feat" ||
    str === "alignment" ||
    str === "min_ability_score" ||
    str === "spellcaster" ||
    str === "spells_of_schools" ||
    str === "spell" ||
    str === "proficiency" ||
    str === "at_least_1_weapon" ||
    str === "at_least_1_language" ||
    str === "level" ||
    str === "pact_boon" ||
    str === "eldritch_invocation" ||
    str === "tags" ||
    str === "dragonmark" ||
    str === "wild_talent" ||
    str === "feature_of_type" ||
    str === "";
}

export type ModType = "Add" | "Add if > 0" | "Multiply" | "Multiply if > 0" | "Replace" | "Replace if Greater" | "Add Bonus Rolls";

// export type FeatTier = "General" | "Origin" | "Fighting Style" | "Epic" | "Dragonmark" | "Wild Talent";
export type FeatTier = "General" | "Origin" | "Epic";

export const isFeatTier = (str: string): str is FeatTier => {
  return str === "General" || 
  str === "Origin" ||
  str === "Epic";
}

export type TimeRestriction = "Every Time" | "1/Turn" | "Once on Your Turn" | "Bonus Action" | "Hide" | "Special";
export type HappensWhen = "On a Hit" | "On a Miss" | "On a Failed Save" | "On a Succeeded Save" | "On Attack" | "On Use" | "Special";
export type DominanceGroupType = "Race/Species" | "Subrace/Lineage" | "Background" | "Eldritch Invocation" | "Class" | "Subclass" | "Feat" | "Item" | "Spell" | "Other" | "";

export type ExtraBaseType = "None" | "Ability Score" | "Damage Type" | "School of Magic" | "Dragon Type" | "Dragon Breed" | "Creature Type" | "Strings" | "Spell List" | "Spell";

export type WorldSetting = "Forgotten Realms" | "Eberron" | "Dragonlance" | "Ravenloft" | "Spelljammer" | "Drakkenheim" | "Dark Sun" | "Planescape" | "Any";

export type BastionFacilitySize = "Cramped" | "Roomy" | "Vast";

export type FeatureType = "Subclass" | "Subrace" | "Modifier" | "Transform Upgrade" | 
  "Reroll" | "Spell Modifier" | "Proficiencies" | "Expertise" | "Feat" | "Selectable" | 
  "Advantage" | "Greater Advantage" | "Damage Multiplier" | "Resource" | "Language" | 
  "Ability Score Improvement" | "Jack of All Trades" | "Extra Attacks" | "Minion Extra Attacks" | 
  "Ability" | "Skill as Ability" | "Skill Ability Override" | "Creature Ability" | 
  "Minion Ability" | "Extra Spell as Ability" | "Item Affecting Ability" | "Item Affecting Ability Type 2" | 
  "Ritual Casting" | "Spellcasting" | "Spell Infusions" | "Spell Infusion Extra Effects" | 
  "Spellcasting Focus" | "Spellbook" | "Spellbook Modifier" | "Cantrips" | "Spells" |  
  "Spells from List" | "Spells from List with Costs" | "Bonus Spells" | "Bonus Spells Upgrade" | "Extra Spell Lists" | 
  "Eldritch Invocations" | "Fighting Style" | "Pact Boon" | "Proficiency Override" | 
  "Ignore Heavy Rule" | "Specific Special Feature" | "Special Feature" | "Special Feature Choices" | 
  "Special Feature List Merge" | "Mystic Arcanum" | "Spell Mastery" | "Sense" | 
  "Extra IAA Slots" | "Condition" | "Alternate Condition" | "Allowed Conditions" | 
  "Start Equipment" | "Unarmed Strikes" | "Resource Refresh Rule Change" | "Replace Ability Score for Attacks" | 
  "Attack Upgrade" | "Versatile Upgrade" | "Weapon Mastery" | "Weapon Mastery Master" | 
  "Extra Effects" | "Extra Effect Type" | "Extra Effects of Type" | "Disable Actions" | 
  "Bonus Action Attack" | "Weapon Keyword Upgrade" | "Weapon Keyword Upgrade 2" | "Weapon Damage Upgrade" | 
  "Characteristic" | "Minimum Roll" | "Suggested Spells" | "Special Feat Access" | 
  "Light Attack Add Damage Mod" | "Summon Upgrade" | "Action Type Upgrade" | "Knowledge" | 
  "Learnable" | "Spell Damage Options" | "Ignore Stealth Disadvantage" | "Size Options" | 
  "Limit Spoof" | "Special Learnable" | "Rest Alteration" | "Creature Type" | "";

export type SourceBookType = "Source Book" | "Setting" | "Adventure" | "Homebrew" | "Unearthed Arcana";

export type MainDataType = 
  "upsertable_base" |
  "model_base" | 
  "feature_source" |
  "player_object";
  
export const isMainDataType = (str: string): str is MainDataType => {
  return str === "upsertable_base" || 
    str === "model_base" ||
    str === "feature_source" ||
    str === "player_object";
}

export type FSType = 
  "feature_source" |
  "condition" | 
  "weapon_keyword" | 
  "item" | 
  "magic_item_keyword" | 
  "feat" | 
  "special_feature" | 
  "gift" | 
  "race" | 
  "subrace" | 
  "background" | 
  "standard_features" |
  // "standard_optional_features" |
  "special_feature" | 
  "sf_special_attributes" |
  "game_class" |
  "subclass" | 
  "pact_boon" | 
  "fighting_style" |
  "eldritch_invocation" |
  "standard_features";

export const isFSType = (str: string): str is FSType => {
  return str === "feature_source" ||
    str === "condition" ||
    str === "weapon_keyword" ||
    str === "item" ||
    str === "magic_item_keyword" ||
    str === "feat" ||
    str === "special_feature" ||
    str === "gift" ||
    str === "race" ||
    str === "subrace" ||
    str === "background" ||
    str === "standard_features" ||
    // str === "standard_optional_features" ||
    str === "special_feature" ||
    str === "sf_special_attributes" ||
    str === "game_class" ||
    str === "subclass" ||
    str === "pact_boon" ||
    str === "fighting_style" ||
    str === "eldritch_invocation" ||
    str === "standard_features";
}

export type PlayerObjectType =
  "campaign" |
  "encounter" |
  "character" |
  "user_ticket" |
  "user" |
  "character_receivable" |
  "campaign_recipe" |
  "character_drawn_card" |
  "org_instance" |
  "campaign_deck" | 
  "complex_creature";

export const isPlayerObjectType = (str: string): str is PlayerObjectType => {
  return str === "campaign" ||
    str === "encounter" ||
    str === "character" ||
    str === "user_ticket" ||
    str === "user" ||
    str === "character_receivable" ||
    str === "campaign_recipe" ||
    str === "character_drawn_card" ||
    str === "org_instance" ||
    str === "campaign_deck" ||
    str === "complex_creature";
}

export type MiscDataType = 
  "spell" | 
  "spell_list" |
  "creature" | 
  "armor_type" | 
  "table" | 
  "proficiency" | 
  "resource" | 
  "sense" | 
  "tool" | 
  "transform" | 
  "summon" | 
  "adaptation" | 
  "optional_feature_base" | 
  "floating_feature" | 
  "creature_feature" |
  "reusable_string" | 
  "template" |
  "source_book" |
  "sb_section" |
  "rollable_table" | 
  "language" | 
  "deity" | 
  "game_deck" | 
  "game_card" |
  "weapon_maneuver" |
  "equipment_pack" |
  "loot_table" |
  "bastion_facility" |
  "ticket" | 
  "action_type" |
  "skill" |
  "creature_group" |
  "creature_gift" | 
  "weapon_mastery_property" |
  "spell_slot_type" | 
  "template_base" |
  "optional_feature_base" | 
  "feature_user_rating" |
  "organization" |
  "org_position";

export const isMiscDataType = (str: string): str is MiscDataType => {
  return str === "spell" || 
    str === "spell_list" || 
    str === "creature" || 
    str === "armor_type" || 
    str === "table" || 
    str === "proficiency" || 
    str === "resource" || 
    str === "sense" || 
    str === "tool" || 
    str === "transform" || 
    str === "summon" || 
    str === "adaptation" || 
    str === "optional_feature_base" || 
    str === "floating_feature" || 
    str === "creature_feature" ||
    str === "reusable_string" || 
    str === "template" || 
    str === "source_book" || 
    str === "sb_section" ||
    str === "rollable_table" || 
    str === "language" || 
    str === "deity" || 
    str === "game_deck" || 
    str === "game_card" || 
    str === "weapon_maneuver" || 
    str === "equipment_pack" || 
    str === "loot_table" || 
    str === "bastion_facility" || 
    str === "ticket" || 
    str === "action_type" || 
    str === "skill" || 
    str === "creature_group" || 
    str === "creature_gift" || 
    str === "weapon_mastery_property" || 
    str === "spell_slot_type" || 
    str === "template_base" || 
    str === "optional_feature_base" || 
    str === "feature_user_rating" ||
    str === "organization" ||
    str === "org_position";
}

export type DataType = 
  MainDataType |
  FSType |
  PlayerObjectType |
  MiscDataType;

export const isDataType = (str: string): str is DataType => {
  return isMainDataType(str) || 
    isFSType(str) || 
    isPlayerObjectType(str) ||
    isMiscDataType(str);
}

export type TicketType = "Bug" | "Suggestion" | "Comment";

export type CostOverrideType = "Don't Override" | "At Will" | "At Will or Slot Upcast" | "Only Special Resource" | "Or Special Resource" | "And Special Resource";

export const isSourceBookType = (str: string): str is SourceBookType => {
  return str === "Source Book" || 
    str === "Setting" || 
    str === "Adventure" || 
    str === "Homebrew" || 
    str === "Unearthed Arcana";
}

export const isDamageType = (str: string): str is DamageType => {
  return str === "Bludgeoning" || 
    str === "Piercing" || 
    str === "Slashing" || 
    str === "Acid" || 
    str === "Cold" || 
    str === "Fire" || 
    str === "Force" || 
    str === "Lightning" || 
    str === "Necrotic" || 
    str === "Poison" || 
    str === "Psychic" || 
    str === "Radiant" || 
    str === "Thunder" || 
    str === "Typeless" || 
    str === "Same as Main";
}

export const isAttackType = (str: string): str is AttackType => {
  return str === "Melee" || 
    str === "Ranged" || 
    str === "Melee Spell" || 
    str === "Ranged Spell" || 
    str === "Bonus" || 
    str === "Save" || 
    str === "Thrown" || 
    str === "Light" || 
    str === "Versatile" || 
    str === "None";
}

export const isStatType = (str: string): str is AbilityScoreType => {
  return str === "STR" ||
    str === "DEX" ||
    str === "CON" ||
    str === "INT" ||
    str === "WIS" ||
    str === "CHA";
}

export const isStatExpandedType = (str: string): str is AbilityScoreExpandedType => {
  return isStatType(str) ||
    str === "Spellcasting" ||
    str === "Same as Feat" ||
    str === "Selectable" ||
    str === "STR or DEX" ||
    str === "INT, WIS, or CHA" ||
    str === "Your Choice" ||
    str === "None" ||
    str === "Attack";
}

export const isSpellLevel = (num: number): num is SpellLevel => {
  return num === - 1 ||
    num === 0 ||
    num === 1 ||
    num === 2 ||
    num === 3 ||
    num === 4 ||
    num === 5 ||
    num === 6 ||
    num === 7 ||
    num === 8 ||
    num === 9;
}

export const isSchoolOfMagic = (str: string): str is SchoolOfMagic => {
  return str === "Conjuration" ||
    str === "Necromancy" ||
    str === "Evocation" ||
    str === "Abjuration" ||
    str === "Transmutation" ||
    str === "Divination" ||
    str === "Enchantment" ||
    str === "Illusion";
}

export const isTicketType = (str: string): str is TicketType => {
  return str === "Bug" ||
    str === "Suggestion" ||
    str === "Comment";
}

export const isCostOverrideType = (str: string): str is CostOverrideType => {
  return str === "Don't Override" ||
    str === "At Will" ||
    str === "At Will or Slot Upcast" ||
    str === "Only Special Resource" ||
    str === "Or Special Resource" ||
    str === "And Special Resource";
}

export type ProficiencyType = "Skill" | "Tool" | "Saving Throw" | "Special" | "Armor" | "Weapon" | "Special Weapon";

export const isProficiencyType = (str: string): str is ProficiencyType => {
  return str === "Skill" ||
    str === "Tool" ||
    str === "Saving Throw" ||
    str === "Special" ||
    str === "Armor" ||
    str === "Weapon" ||
    str === "Special Weapon";
}